import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getEmailConfigs,
  getActiveEmailConfig,
  createEmailConfig,
  updateEmailConfig,
  deleteEmailConfig,
  setActiveEmailConfig,
  EmailConfig,
  EmailConfigInput,
} from '@/services/admin/email-admin.api';

export default function EmailConfigPage() {
  const { t } = useTranslation();
  const [configs, setConfigs] = useState<EmailConfig[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<EmailConfig | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<EmailConfigInput>({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_pass: '',
    from_email: '',
    from_name: 'GTRA',
    to_email: 'info@gtraweb3.com',
    is_active: true,
  });

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    setLoading(true);
    try {
      const [configsData, activeConfig] = await Promise.all([
        getEmailConfigs(),
        getActiveEmailConfig(),
      ]);
      setConfigs(configsData);
      setActiveId(activeConfig?.id || null);
    } catch (error) {
      console.error('Failed to load email configs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editing) {
        await updateEmailConfig(editing.id, formData);
      } else {
        await createEmailConfig(formData);
      }
      setShowForm(false);
      setEditing(null);
      setFormData({
        smtp_host: '',
        smtp_port: 587,
        smtp_user: '',
        smtp_pass: '',
        from_email: '',
        from_name: 'GTRA',
        to_email: 'info@gtraweb3.com',
        is_active: true,
      });
      loadConfigs();
    } catch (error) {
      console.error('Failed to save email config:', error);
    }
  };

  const handleEdit = (config: EmailConfig) => {
    setEditing(config);
    setFormData({
      smtp_host: config.smtp_host,
      smtp_port: config.smtp_port,
      smtp_user: config.smtp_user,
      smtp_pass: '', // 不复制加密后的密码，让用户重新输入
      from_email: config.from_email,
      from_name: config.from_name,
      to_email: config.to_email,
      is_active: config.is_active,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this configuration?')) return;
    try {
      await deleteEmailConfig(id);
      loadConfigs();
    } catch (error) {
      console.error('Failed to delete email config:', error);
    }
  };

  const handleSetActive = async (id: string) => {
    try {
      await setActiveEmailConfig(id);
      setActiveId(id);
    } catch (error) {
      console.error('Failed to set active config:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('admin.emailConfig.title')}</h1>
          <p className="text-gray-500 mt-1">{t('admin.emailConfig.description')}</p>
        </div>
        <button
          onClick={() => {
            setEditing(null);
            setFormData({
              smtp_host: '',
              smtp_port: 587,
              smtp_user: '',
              smtp_pass: '',
              from_email: '',
              from_name: 'GTRA',
              to_email: 'info@gtraweb3.com',
              is_active: true,
            });
            setShowForm(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          {t('admin.emailConfig.add')}
        </button>
      </div>

      {/* Email Config List */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          {configs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              {t('admin.emailConfig.noConfigs')}
            </div>
          ) : (
            <div className="space-y-4">
              {configs.map((config) => (
                <div
                  key={config.id}
                  className={`p-4 rounded-lg border-2 ${
                    activeId === config.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{config.from_name}</h3>
                        {activeId === config.id && (
                          <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                            {t('admin.emailConfig.active')}
                          </span>
                        )}
                      </div>
                      <div className="mt-2 text-sm text-gray-600 space-y-1">
                        <p>
                          <span className="font-medium">SMTP:</span> {config.smtp_host}:{config.smtp_port}
                        </p>
                        <p>
                          <span className="font-medium">{t('admin.emailConfig.from')}:</span> {config.from_email}
                        </p>
                        <p>
                          <span className="font-medium">{t('admin.emailConfig.to')}:</span> {config.to_email}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {activeId !== config.id && (
                        <button
                          onClick={() => handleSetActive(config.id)}
                          className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
                        >
                          {t('admin.emailConfig.setActive')}
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(config)}
                        className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                      >
                        {t('admin.emailConfig.edit')}
                      </button>
                      <button
                        onClick={() => handleDelete(config.id)}
                        className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        {t('admin.emailConfig.delete')}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Email Config Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">
                {editing ? t('admin.emailConfig.editConfig') : t('admin.emailConfig.addConfig')}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('admin.emailConfig.smtpHost')}
                  </label>
                  <input
                    type="text"
                    value={formData.smtp_host}
                    onChange={(e) => setFormData({ ...formData, smtp_host: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="smtp.gmail.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('admin.emailConfig.smtpPort')}
                  </label>
                  <input
                    type="number"
                    value={formData.smtp_port}
                    onChange={(e) => setFormData({ ...formData, smtp_port: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="587"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('admin.emailConfig.smtpUser')}
                  </label>
                  <input
                    type="text"
                    value={formData.smtp_user}
                    onChange={(e) => setFormData({ ...formData, smtp_user: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="your-email@gmail.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('admin.emailConfig.smtpPass')}
                  </label>
                  <input
                    type="password"
                    value={formData.smtp_pass}
                    onChange={(e) => setFormData({ ...formData, smtp_pass: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder={editing ? '(unchanged)' : 'password'}
                    required={!editing}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('admin.emailConfig.fromEmail')}
                  </label>
                  <input
                    type="email"
                    value={formData.from_email}
                    onChange={(e) => setFormData({ ...formData, from_email: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="noreply@gtraweb3.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('admin.emailConfig.fromName')}
                  </label>
                  <input
                    type="text"
                    value={formData.from_name}
                    onChange={(e) => setFormData({ ...formData, from_name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="GTRA"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.emailConfig.toEmail')}
                </label>
                <input
                  type="email"
                  value={formData.to_email}
                  onChange={(e) => setFormData({ ...formData, to_email: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="info@gtraweb3.com"
                  required
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <label htmlFor="is_active" className="text-sm text-gray-700">
                  {t('admin.emailConfig.setAsActive')}
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditing(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  {t('admin.emailConfig.cancel')}
                </button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  {t('admin.emailConfig.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
